/**
 * Field-of-view feel (plan G9): an eased boost kick plus a subtle speed widening. Pure and allocation-free;
 * the feature adds the result to the base FOV from world.json.
 */
import type { CameraFovTuning } from './tuning'

export interface FovState {
  /** Linear boost-kick progress 0..1 (eased on output). */
  kick: number
  /** Smoothed speed FOV in degrees. */
  speedDeg: number
}

export function createFovState(): FovState {
  return { kick: 0, speedDeg: 0 }
}

/** Cubic ease-in-out on 0..1, clamped: zero slope at both ends, so the kick has no visible start or stop. */
export function smoothstep(t: number): number {
  if (t <= 0) return 0
  if (t >= 1) return 1
  return t * t * (3 - 2 * t)
}

/** The speed FOV (degrees) the camera settles to at `speedFraction` of the ship's top boosted speed. */
export function speedFovTarget(t: CameraFovTuning, speedFraction: number): number {
  return t.speedMaxDeg * smoothstep((speedFraction - t.speedFromFraction) / (1 - t.speedFromFraction))
}

/** Advances the state by `dt` seconds and returns the extra FOV in degrees (boost kick + speed). */
export function stepFov(s: FovState, t: CameraFovTuning, boosting: boolean, speedFraction: number, dt: number): number {
  if (dt > 0) {
    s.kick = boosting ? Math.min(1, s.kick + dt / t.kickInSeconds) : Math.max(0, s.kick - dt / t.kickOutSeconds)
    const target = speedFovTarget(t, speedFraction)
    s.speedDeg += (target - s.speedDeg) * (1 - Math.exp(-t.speedSmoothing * dt))
  }
  return t.boostKickDeg * smoothstep(s.kick) + s.speedDeg
}
